import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Button,
  Divider,
  TextField,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import { CollectionFilters, ActiveFilter } from '../types/filters'
import {
  WRAPPER_TYPE_OPTIONS,
  STRENGTH_LEVEL_OPTIONS,
  STATUS_OPTIONS,
  ORIGIN_OPTIONS,
} from '../constants/filterOptions'
import { CollectionSearch } from './CollectionSearch'
import { useCollectionSearch } from '../hooks/useCollectionSearch'
import { COLLECTION_SEARCH_DEBOUNCE_MS } from '../constants/debounce'

interface CollectionFiltersProps {
  filters: CollectionFilters
  activeFilters: ActiveFilter[]
  hasActiveFilters: boolean
  brands?: Array<{ id: string; name: string }>
  vitolas?: Array<{ id: string; name: string }>
  onFilterChange: (key: keyof CollectionFilters, value: string | undefined) => void
  onClearFilters: () => void
}

export const CollectionFiltersPanel: React.FC<CollectionFiltersProps> = ({
  filters,
  activeFilters,
  hasActiveFilters,
  brands = [],
  vitolas = [],
  onFilterChange,
  onClearFilters,
}) => {
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true })
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  useEffect(() => {
    setFiltersExpanded(isMdUp)
  }, [isMdUp])

  const handleSearch = useCallback(
    (value: string) => {
      onFilterChange('search', value || undefined)
    },
    [onFilterChange]
  )

  const { inputValue, handleChange, handleClear } = useCollectionSearch(
    filters.search || '',
    handleSearch,
    COLLECTION_SEARCH_DEBOUNCE_MS
  )

  const showFilterToggle = !isMdUp

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box mb={showFilterToggle || isMdUp ? 2 : 0}>
          <CollectionSearch
            value={inputValue}
            onChange={handleChange}
            onClear={handleClear}
          />
        </Box>

        {showFilterToggle && (
          <Button
            size="small"
            onClick={() => setFiltersExpanded((prev) => !prev)}
            endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: filtersExpanded ? 2 : 1, minHeight: 44 }}
            aria-expanded={filtersExpanded}
            aria-controls="collection-filters-panel"
          >
            {filtersExpanded ? 'Hide filters' : 'Show filters'}
          </Button>
        )}

        <Collapse
          id="collection-filters-panel"
          in={filtersExpanded || isMdUp}
          timeout="auto"
          unmountOnExit={false}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="collection-filter-brand-label">Brand</InputLabel>
                <Select
                  labelId="collection-filter-brand-label"
                  id="collection-filter-brand"
                  label="Brand"
                  inputProps={{ 'aria-label': 'Filter by brand' }}
                  value={filters.brandId || ''}
                  onChange={(e) => onFilterChange('brandId', e.target.value || undefined)}
                >
                  <MenuItem value="">All Brands</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="collection-filter-wrapper-label">Wrapper</InputLabel>
                <Select
                  labelId="collection-filter-wrapper-label"
                  id="collection-filter-wrapper"
                  label="Wrapper"
                  inputProps={{ 'aria-label': 'Filter by wrapper type' }}
                  value={filters.wrapperType || ''}
                  onChange={(e) => onFilterChange('wrapperType', e.target.value || undefined)}
                >
                  <MenuItem value="">All Wrappers</MenuItem>
                  {WRAPPER_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="collection-filter-strength-label">Strength</InputLabel>
                <Select
                  labelId="collection-filter-strength-label"
                  id="collection-filter-strength"
                  label="Strength"
                  inputProps={{ 'aria-label': 'Filter by strength' }}
                  value={filters.strengthLevel || ''}
                  onChange={(e) => onFilterChange('strengthLevel', e.target.value || undefined)}
                >
                  <MenuItem value="">All Strengths</MenuItem>
                  {STRENGTH_LEVEL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="collection-filter-status-label">Status</InputLabel>
                <Select
                  labelId="collection-filter-status-label"
                  id="collection-filter-status"
                  label="Status"
                  inputProps={{ 'aria-label': 'Filter by status' }}
                  value={filters.status || ''}
                  onChange={(e) => onFilterChange('status', e.target.value || undefined)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {vitolas.length > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="collection-filter-vitola-label">Size/Vitola</InputLabel>
                  <Select
                    labelId="collection-filter-vitola-label"
                    id="collection-filter-vitola"
                    label="Size/Vitola"
                    inputProps={{ 'aria-label': 'Filter by vitola size' }}
                    value={filters.vitolaId || ''}
                    onChange={(e) => onFilterChange('vitolaId', e.target.value || undefined)}
                  >
                    <MenuItem value="">All Sizes</MenuItem>
                    {vitolas.map((vitola) => (
                      <MenuItem key={vitola.id} value={vitola.id}>
                        {vitola.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="collection-filter-origin-label">Origin</InputLabel>
                <Select
                  labelId="collection-filter-origin-label"
                  id="collection-filter-origin"
                  label="Origin"
                  inputProps={{ 'aria-label': 'Filter by origin country' }}
                  value={filters.origin || ''}
                  onChange={(e) => onFilterChange('origin', e.target.value || undefined)}
                >
                  <MenuItem value="">All Origins</MenuItem>
                  {ORIGIN_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Purchased From"
                type="date"
                value={filters.purchaseDateStart || ''}
                onChange={(e) => onFilterChange('purchaseDateStart', e.target.value || undefined)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Purchased To"
                type="date"
                value={filters.purchaseDateEnd || ''}
                onChange={(e) => onFilterChange('purchaseDateEnd', e.target.value || undefined)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Collapse>

        {(hasActiveFilters || activeFilters.length > 0) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              data-testid="collection-filter-chips"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                flexWrap: 'nowrap',
                maxWidth: '100%',
                pb: 0.5,
                mx: -0.5,
                px: 0.5,
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'nowrap' }}>
                {activeFilters.map((filter) => (
                  <Chip
                    key={filter.key}
                    label={filter.label}
                    size="small"
                    onDelete={() => onFilterChange(filter.key, undefined)}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                ))}
                {hasActiveFilters && (
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={onClearFilters}
                    color="inherit"
                    sx={{ flexShrink: 0, minHeight: 44 }}
                  >
                    Clear all
                  </Button>
                )}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}
